import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { resolveUrl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Play, Clock, CheckCircle2, Circle, Award, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function JourneyDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [journey, setJourney] = useState(null);
    const [progress, setProgress] = useState(null);
    const [billing, setBilling] = useState(null);

    useEffect(() => {
        api.get(`/journeys/${slug}`).then(r => setJourney(r.data));
    }, [slug]);

    useEffect(() => {
        if (user && journey) {
            api.get(`/progress/${journey.id}`).then(r => setProgress(r.data)).catch(() => {});
            api.get(`/billing/me`).then(r => setBilling(r.data)).catch(() => {});
        }
    }, [user, journey]);

    if (!journey) return (
        <div className="min-h-screen"><Header /><div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 font-body text-[#5C5C5C]">Carregando...</div></div>
    );

    const completedSet = new Set((progress?.items || []).filter(i => i.completed).map(i => i.module_id));
    // Flatten all lessons across modules — kept in order
    const allLessons = journey.modules.flatMap(m => (m.lessons || []).map(l => ({ ...l, _module: m })));
    const totalLessons = allLessons.length;
    const nextLesson = allLessons.find(l => !completedSet.has(l.id)) || allLessons[0];

    const isLocked = !journey.is_free && user && billing && !billing.has_access;

    const startWatch = () => {
        if (!user) return navigate("/login", { state: { from: `/jornada/${slug}` } });
        if (isLocked) return navigate("/planos");
        if (!nextLesson) return;
        navigate(`/assistir/${slug}/${nextLesson.id}`);
    };

    return (
        <div className="min-h-screen relative grain">
            <Header />

            <section data-testid="journey-hero" className="relative overflow-hidden">
                <img src={resolveUrl(journey.cover)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#F8F8F5] via-[#F8F8F5]/70 to-[#F8F8F5]/20" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-16">
                    <div className="max-w-3xl">
                        <div className="overline text-[#5C5C5C]">{journey.category?.name}</div>
                        <h1 className="font-display font-light text-4xl md:text-6xl mt-3 leading-tight">{journey.title}</h1>
                        <p className="font-editorial italic text-2xl md:text-3xl text-[#1A1A1A] mt-4">{journey.subtitle}</p>

                        <div className="flex flex-wrap gap-6 mt-8 font-body text-sm text-[#5C5C5C]">
                            <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {Math.round(journey.total_minutes/60*10)/10} horas</span>
                            <span className="flex items-center gap-2"><Award className="w-4 h-4" /> Certificado ao concluir</span>
                            <span className="flex items-center gap-2">{journey.modules.length} {journey.modules.length === 1 ? "módulo" : "módulos"} · {totalLessons} {totalLessons === 1 ? "aula" : "aulas"}</span>
                        </div>

                        {progress && progress.total_modules > 0 && (
                            <div className="mt-6 max-w-md">
                                <div className="flex justify-between font-body text-xs text-[#5C5C5C] mb-2">
                                    <span>Seu progresso</span><span>{progress.percent}%</span>
                                </div>
                                <div className="bar"><div style={{ width: `${progress.percent}%` }} /></div>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3 mt-8">
                            <button data-testid="journey-start" onClick={startWatch} className="btn-primary">
                                {isLocked ? <Lock className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
                                {isLocked
                                    ? "Assinar para continuar"
                                    : (progress?.completed_modules ? "Continuar jornada" : "Começar jornada")}
                            </button>
                            {isLocked && (
                                <span data-testid="journey-locked-hint" className="font-body text-sm text-[#5C5C5C] self-center">
                                    Seu acesso gratuito terminou — assine para acessar todas as jornadas premium.
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-12 grid lg:grid-cols-3 gap-12">
                {/* Description + Modules list */}
                <div className="lg:col-span-2">
                    {journey.description && (
                        <div data-testid="journey-description" className="mb-12">
                            <div className="overline text-[#5C5C5C]">Sobre a jornada</div>
                            <h2 className="font-display text-3xl mt-1 tracking-tight">Descrição</h2>
                            <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 mt-6">
                                <p className="font-body text-[#1A1A1A]/85 text-base md:text-lg leading-relaxed whitespace-pre-line">
                                    {journey.description}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="overline text-[#5C5C5C]">Conteúdo da jornada</div>
                    <h2 className="font-display text-3xl mt-1 tracking-tight">Módulos</h2>
                    <div className="mt-6 space-y-5">
                        {journey.modules.map((m) => {
                            const lessons = m.lessons || [];
                            const doneCount = lessons.filter(l => completedSet.has(l.id)).length;
                            const modMinutes = lessons.reduce((a, l) => a + (l.duration_minutes || 0), 0);
                            return (
                                <div key={m.id} data-testid={`module-block-${m.order}`} className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                                    <div className="flex items-center gap-4 p-5 border-b border-black/5">
                                        <div className="w-11 h-11 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-display text-sm">
                                            {String(m.order).padStart(2, "0")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="overline text-[#5C5C5C]">Módulo</div>
                                            <h3 className="font-display text-xl leading-tight">{m.title}</h3>
                                            {m.description && <p className="font-body text-sm text-[#5C5C5C] mt-1 line-clamp-2">{m.description}</p>}
                                        </div>
                                        <div className="text-right font-body text-xs text-[#5C5C5C] hidden sm:block">
                                            <div>{lessons.length} {lessons.length === 1 ? "aula" : "aulas"} · {modMinutes} min</div>
                                            {user && lessons.length > 0 && (
                                                <div className="mt-1 inline-flex items-center gap-1 font-display">
                                                    {doneCount}/{lessons.length} concluídas
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <ol className="divide-y divide-black/5">
                                        {lessons.map((l) => {
                                            const done = completedSet.has(l.id);
                                            return (
                                                <li key={l.id}>
                                                    <Link
                                                        to={user ? `/assistir/${slug}/${l.id}` : "/login"}
                                                        data-testid={`lesson-item-${m.order}-${l.order}`}
                                                        className="flex items-center gap-4 px-5 py-4 hover:bg-[#FAFAF7] transition-colors"
                                                    >
                                                        <div className="w-9 text-center font-display text-xs text-[#5C5C5C]">{m.order}.{l.order}</div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-display text-base leading-snug truncate">{l.title}</div>
                                                            {l.description && <div className="font-body text-xs text-[#5C5C5C] mt-0.5 line-clamp-1">{l.description}</div>}
                                                        </div>
                                                        <div className="font-body text-xs text-[#5C5C5C] hidden md:flex items-center gap-1 shrink-0">
                                                            <Clock className="w-3 h-3" />{l.duration_minutes}min
                                                        </div>
                                                        {done
                                                            ? <CheckCircle2 className="w-5 h-5 text-[#00D4D4] shrink-0" />
                                                            : <Circle className="w-5 h-5 text-[#1A1A1A]/20 shrink-0" />}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                        {lessons.length === 0 && (
                                            <li className="px-5 py-6 text-center font-body text-sm text-[#5C5C5C]">
                                                Nenhuma aula publicada neste módulo ainda.
                                            </li>
                                        )}
                                    </ol>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Mentor */}
                <aside data-testid="mentor-card" className="lg:sticky lg:top-[88px] self-start">
                    <div className="overline text-[#5C5C5C]">Mentor</div>
                    <div className="bg-white rounded-3xl border border-black/5 p-6 mt-2">
                        <img src={resolveUrl(journey.mentor?.photo)} alt={journey.mentor?.name} className="w-full aspect-[4/5] object-cover rounded-2xl" />
                        <h3 className="font-display text-xl mt-5">{journey.mentor?.name}</h3>
                        <div className="font-body text-xs text-[#5C5C5C] mt-1">{journey.mentor?.title}</div>
                        <p className="font-body text-sm text-[#5C5C5C] mt-4">{journey.mentor?.bio}</p>
                        <div className="flex flex-wrap gap-2 mt-5">
                            {(journey.mentor?.specialties || []).map(s => (
                                <span key={s} className="text-xs font-display px-3 py-1.5 rounded-full bg-[#F8F8F5] border border-black/5">{s}</span>
                            ))}
                        </div>
                    </div>
                </aside>
            </section>

            <Footer />
        </div>
    );
}
